import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import router from '@/router'
import { authService } from '@/services/authService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { setOfflineAccountContext } from '@/services/offlineAuthBridge'
import { syncService } from '@/services/syncService'
import { showError } from '@/services/toastService'
import { uiSettingsSyncService } from '@/services/uiSettingsSyncService'
import { useEventStore } from '@/stores/eventStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useOfflineStore } from '@/stores/offlineStore'
import { usePhotoStore } from '@/stores/photoStore'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type {
  AcceptInvitationRequest,
  AuthState,
  EditProfileRequest,
  LoginRequest,
  PasswordResetRequest,
  PasswordResetConfirmRequest,
  SignupRequest,
  User,
} from '@/types/user'

const AUTH_STORAGE_KEY = 'routecamp-auth'

function combineErrors(message: string, errors: unknown[]): Error {
  const details = errors
    .map((error) => (error instanceof Error ? error.message : String(error)))
    .join('; ')
  return new Error(details ? `${message}: ${details}` : message)
}

export const useAuthStore = defineStore('auth', () => {
  // Reactive state
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Computed getters
  const isAuthenticated = computed(() => !!user.value)

  const authState = computed<AuthState>(() => ({
    isAuthenticated: isAuthenticated.value,
    user: user.value,
    isLoading: isLoading.value,
    error: error.value,
  }))

  async function lockPersistentAccountContexts(expectedOwnerUserId?: number): Promise<void> {
    const eventStore = useEventStore()
    const eventLock =
      expectedOwnerUserId === undefined
        ? eventStore.lockForLogout()
        : eventStore.lockForLogout(expectedOwnerUserId)
    const results = await Promise.allSettled([setOfflineAccountContext(null, null), eventLock])
    const failures = results
      .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
      .map((result) => result.reason)
    if (failures.length > 0) {
      throw combineErrors('Failed to lock account-owned storage', failures)
    }
  }

  // Initialize auth state from localStorage
  async function init() {
    try {
      // Native and IndexedDB work can outlive a webview reload; lock both before restoring auth.
      syncService.invalidateEventQueueOwner()
      await lockPersistentAccountContexts()
      const savedAuth = localStorage.getItem(AUTH_STORAGE_KEY)
      if (savedAuth) {
        const parsedAuth = JSON.parse(savedAuth) as { user: User }
        if (parsedAuth.user && parsedAuth.user.userId && parsedAuth.user.email) {
          await setUser(
            {
              userId: parsedAuth.user.userId,
              email: parsedAuth.user.email,
              name: parsedAuth.user.name || null,
              sessionToken: parsedAuth.user.sessionToken || null,
            },
            false,
          )
        }
      }
    } catch (initError) {
      user.value = null
      localStorage.removeItem(AUTH_STORAGE_KEY)
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to restore authenticated account', {
        error: initError,
      })
    }
  }

  // Save auth state to localStorage
  function saveAuthState() {
    if (user.value) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: user.value }))
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY)
    }
  }

  // Clear error state
  function clearError() {
    error.value = null
  }

  function resetPrivateAccountState() {
    const workspaceStore = useWorkspaceStore()
    const photoStore = usePhotoStore()
    const libraryStore = useLibraryStore()
    const offlineStore = useOfflineStore()
    const searchWorkspaceStore = useSearchWorkspaceStore()

    workspaceStore.clearToOverviewState()
    photoStore.$reset()
    libraryStore.reset()
    offlineStore.resetForAccountChange()
    searchWorkspaceStore.resetPrivateWorkspaceCatalog()
  }

  // Set user and save to localStorage
  async function setUser(userData: User, initializeOfflineStore = true): Promise<void> {
    const previousUserId = user.value?.userId
    const accountChanged = previousUserId !== userData.userId
    if (accountChanged) {
      syncService.invalidateWorkspaceLoads()
      syncService.invalidateEventQueueOwner()
    }
    const activationResults = await Promise.allSettled([
      setOfflineAccountContext(userData.userId, userData.sessionToken ?? null),
      useEventStore().activateForOwner(userData.userId),
    ])
    const activationFailures = activationResults
      .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
      .map((result) => result.reason)
    if (activationFailures.length > 0) {
      syncService.invalidateWorkspaceLoads()
      syncService.invalidateEventQueueOwner()
      user.value = null
      saveAuthState()
      resetPrivateAccountState()

      try {
        await lockPersistentAccountContexts(userData.userId)
      } catch (lockError) {
        logService.error(
          LOG_CATEGORIES.GENERAL,
          'Failed to lock account-owned storage after account activation failed',
          { activationFailures, lockError },
        )
        throw combineErrors('Account activation and storage recovery failed', [
          ...activationFailures,
          lockError,
        ])
      }
      throw combineErrors('Failed to activate account-owned storage', activationFailures)
    }
    if (previousUserId !== undefined && accountChanged) {
      resetPrivateAccountState()
    }
    user.value = userData
    saveAuthState()
    clearError()
    if (initializeOfflineStore) {
      await useOfflineStore().activateForOwner(userData.userId)
    } else {
      await useOfflineStore().activateForOwner(userData.userId, false)
    }
  }

  // Clear user data and remove from localStorage
  async function clearUser(): Promise<void> {
    const previousUserId = user.value?.userId
    syncService.invalidateWorkspaceLoads()
    syncService.invalidateEventQueueOwner()
    user.value = null
    saveAuthState()
    resetPrivateAccountState()
    await lockPersistentAccountContexts(previousUserId)
  }

  // Sign up a new user
  async function signup(signupData: SignupRequest): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.signup(signupData)

      if (response.success && response.userId && response.email) {
        await setUser({
          userId: response.userId,
          email: response.email,
          name: response.name || null,
          sessionToken: null,
        })

        // Kick off post-auth sync before the UI navigates away from the auth flow.
        await Promise.all([
          syncService.handleAuthenticationRecovery(),
          uiSettingsSyncService.synchronize(),
        ])

        return true
      } else {
        error.value = response.message || 'Signup failed'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Signup failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Log in a user
  async function login(loginData: LoginRequest): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.login(loginData)

      if (response.success && response.userId && response.email) {
        await setUser({
          userId: response.userId,
          email: response.email,
          name: response.name || null,
          sessionToken: response.sessionToken ?? null,
        })

        // Kick off post-auth sync before the UI navigates away from the auth flow.
        await Promise.all([
          syncService.handleAuthenticationRecovery(),
          uiSettingsSyncService.synchronize(),
        ])

        return true
      } else {
        error.value = response.message || 'Login failed'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Log out the current user
  async function logout(): Promise<boolean> {
    isLoading.value = true
    error.value = null

    // Determine before clearing state whether we should stay on this page.
    // Stay only if the current workspace is public (accessible without auth).
    const workspaceStore = useWorkspaceStore()
    const onPublicWorkspace =
      !!router.currentRoute.value.params.workspaceId && workspaceStore.isPublic

    try {
      await uiSettingsSyncService.synchronize()
      const response = await authService.logout()

      if (response.success) {
        await clearUser()
        if (!onPublicWorkspace) {
          router.push({ name: 'pinned' })
        }
        return true
      } else {
        error.value = response.message || 'Logout failed'
        return false
      }
    } catch (err) {
      // Even if the API call fails, clear local state
      let logoutError: Error = err instanceof Error ? err : new Error('Logout failed')
      try {
        await clearUser()
      } catch (clearError) {
        logService.error(LOG_CATEGORIES.GENERAL, 'Failed to lock storage during logout', {
          error: clearError,
        })
        logoutError = combineErrors('Logout and local storage locking failed', [err, clearError])
      }
      if (!onPublicWorkspace) {
        router.push({ name: 'pinned' })
      }
      showError(logoutError.message)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Request password reset
  async function requestPasswordReset(resetData: PasswordResetRequest): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.requestPasswordReset(resetData)

      if (response.success) {
        return true
      } else {
        error.value = response.message || 'Password reset request failed'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Password reset request failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Confirm password reset
  async function confirmPasswordReset(resetData: PasswordResetConfirmRequest): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.confirmPasswordReset(resetData)

      if (response.success) {
        return true
      } else {
        error.value = response.message || 'Password reset failed'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Password reset failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Force logout (for use when session expires, etc.)
  async function forceLogout(): Promise<void> {
    const workspaceStore = useWorkspaceStore()
    const onPublicWorkspace =
      !!router.currentRoute.value.params.workspaceId && workspaceStore.isPublic
    try {
      await clearUser()
    } catch (clearError) {
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to lock storage during forced logout', {
        error: clearError,
      })
    }
    clearError()
    if (!onPublicWorkspace) {
      router.push({ name: 'pinned' })
    }
  }

  // Edit user profile
  async function editProfile(profileData: EditProfileRequest): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.editProfile(profileData)

      if (response.success && response.userId && response.email) {
        // Update user data with new profile information
        await setUser({
          userId: response.userId,
          email: response.email,
          name: response.profileName || null,
          sessionToken: user.value?.sessionToken ?? null,
        })
        return true
      } else {
        error.value = response.message || 'Profile update failed'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Profile update failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Accept invitation and authenticate user if new user was created
  async function acceptInvitation(
    token: string,
    invitationData?: AcceptInvitationRequest,
  ): Promise<{ success: boolean; workspaceClientId?: string }> {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.acceptInvitation(token, invitationData)

      if (response.success) {
        // If a new user was created (implied by userId and userEmail being present), authenticate them
        if (response.userId && response.userEmail) {
          await setUser({
            userId: response.userId,
            email: response.userEmail,
            name: response.userProfileName || null,
            sessionToken: user.value?.sessionToken ?? null,
          })

          // Trigger sync of pending events after successful user creation via invitation
          await syncService.handleAuthenticationRecovery()
        }
        return {
          success: true,
          workspaceClientId: response.workspaceClientId,
        }
      } else {
        error.value = response.message || 'Invitation acceptance failed'
        return { success: false }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Invitation acceptance failed'
      return { success: false }
    } finally {
      isLoading.value = false
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (storageEvent) => {
      if (
        storageEvent.key !== AUTH_STORAGE_KEY ||
        storageEvent.newValue === storageEvent.oldValue
      ) {
        return
      }

      let nextUserId: number | null = null
      if (storageEvent.newValue) {
        try {
          const parsed = JSON.parse(storageEvent.newValue) as { user?: { userId?: unknown } }
          nextUserId =
            typeof parsed.user?.userId === 'number' && Number.isSafeInteger(parsed.user.userId)
              ? parsed.user.userId
              : null
        } catch (parseError) {
          logService.warn(LOG_CATEGORIES.GENERAL, 'Ignoring malformed cross-tab auth state', {
            error: parseError,
          })
        }
      }
      if (nextUserId !== null && nextUserId === user.value?.userId) return

      syncService.invalidateWorkspaceLoads()
      syncService.invalidateEventQueueOwner()
      user.value = null
      resetPrivateAccountState()
      useEventStore().lockLocally()
    })
  }

  return {
    // State
    user,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    authState,

    // Actions
    init,
    setUser,
    signup,
    login,
    logout,
    requestPasswordReset,
    confirmPasswordReset,
    editProfile,
    acceptInvitation,
    clearError,
    forceLogout,
    clearUser,
  }
})
